<template lang="pug">
    .games-wrapper(
        ref='gameWrapper',
        :class="{\
            'games-wrapper--solo': (soloGame),\
            'games-wrapper--without-background': isGameWithoutBackground,\
            'games-wrapper--white-background':  isWhiteBackgroundNeeded, \
            'games-wrapper--multiplayer': (playersNumber && playersNumber !== 1)\
        }"
    )
        draggable.games-wrapper__box-for-multiplayer(
            v-if="playersNumber && playersNumber !== 1"
        )
            transition(name="slide-fade" @afterEnter="afterEnter")
                abacus.games-wrapper__abacus(
                    v-if="isActiveAbacusButton && showDraggableContent"
                    :game-difficult="gameDifficult",
                    :number="currentAbacusNumber",
                    @number="changeAbacusNumber",
                    @resetAbacusNumber="resetAbacusNumber",
                )
                tips.games-wrapper__tips(
                    v-if="isActiveTipsButton && showDraggableContent"
                    :tips="tipsForCurrentParameters",
                    :for-multiplayer="true"
                )
            .games-wrapper__button(
                @click="changeShowDraggableContentVariable",
                @mousedown="handleMousedown",
            )
                .games-wrapper__abacus-button(
                    v-if="isActiveAbacusButton"
                    :class="{'games-wrapper__abacus-button--active': showDraggableContent}"
                )
                    svg-icon.abacus__arrow-icon(
                        :name="showDraggableContent ? 'angle-right' : 'angle-left'",
                        size="small",
                    )
                    svg-icon.abacus__icon(name="abacus", size="big")
                    span.abacus__value {{ $i18n.t('trainer.trainer_main_abacus') }}
                tip-button.games-wrapper__tip-button(
                    :class="{'games-wrapper__tip-button_active': showDraggableContent}"
                    v-if="isActiveTipsButton"
                )
        template(v-if='!exerciseId && playersNumber && playersNumber !== 1')
            transition-group.multiplayer(name="fade" @leave="leaveElementAnimation" :class="multiplayerClasses")
                .multiplayer__item(
                    v-for='number in playersNumber',
                    :key='number'
                    :class="classForCurrentAmountOfPlayers(number)"
                )
                    .games-wrapper__content(
                        :class="{'games-wrapper__content--small': isColumnsGame}"
                        :style="{'border-color': isColumnsGame ? colorsForStudents[number - 1] : multiplayerBorderColor }"
                    )
                        div(
                            :is='type',
                            :type='type',
                            :exercise-id='exerciseId',
                            :place='number',
                            :players-number='playersNumber',
                            :show-turtle='showTurtle',
                            @setMotion="$emit('setMotion', $event)",
                            @showTurtle="$emit('showTurtle', $event)",
                            :answer-duration='answerDuration',
                            :check-multiplayer-answers='checkMultiplayerAnswers',
                            :play-again='playAgain',
                            @playAgainReady="$emit('playAgainReady', $event)",
                            @checkAnswersReady="$emit('checkAnswersReady', $event)",
                            :multiplayer-games-state="multiplayerGamesState",
                            :color-for-student="colorsForStudents[number - 1]",
                            :refresh-game-event="refreshGameEvent",
                            :continue-game-event="continueGameEvent",
                            :common-parameters-changer-counter="commonParametersChangerCounter",
                            :is-multiplayer="true"
                            @isCountdownActive="handleIsCountdownActive",
                        )
        div(
            v-else
            :is='type',
            :type='type',
            :exercise-id='exerciseId',
            :place='1',
            :players-number='playersNumber',
            :class="{'four-screen': playersNumber === 4}",
            :show-turtle='showTurtle',
            @showTurtle="$emit('showTurtle', $event)",
            @setMotion="$emit('setMotion', $event)",
            @finish="$emit('finish', $event)",
            :game-type='gameType',
            :answer-duration='answerDuration',
            :check-multiplayer-answers='checkMultiplayerAnswers',
            :play-again='playAgain',
            @playAgainReady="$emit('playAgainReady', $event)",
            @checkAnswersReady="$emit('checkAnswersReady', $event)",
            @setBackground="isGameWithoutBackground = $event",
            :is-multiplayer="false"
        )
            next-exercise(
                v-if="isHomework"
                :homework-id="homeworkId"
                :exercise-id="exerciseId"
            )
</template>

<script>
    import NextExercise from '../NextExercise.vue';
    import Tips from './Tips.vue';
    import TipButton from './TipButton.vue';
    const AdditionAndSubtraction = () => import('./addition-and-subtraction/PlayBox.vue');
    const AdditionAndSubtractionWithTime = () => import('./addition-and-subtraction-with-time/PlayBox.vue');
    const AdditionAndSubtractionMathMagic = () => import('./addition-and-subtraction-math-magic/PlayBox.vue');
    const FlashCards = () => import('./flash-cards/PlayBox.vue');
    const InteractiveFlashCards = () => import('./interactive-flash-cards/PlayBox.vue');
    const MultiplicationAndDivision = () => import('./multiplication-and-division/PlayBox.vue');
    const MultiplicationAndDivisionMathMagic = () => import('./multiplication-and-division-math-magic/PlayBox.vue');
    const TabularMultiplication = () => import('./tabular-multiplication/PlayBox.vue');
    const Equation = () => import('./equation/PlayBox.vue');
    const Factorization = () => import('./factorization/PlayBox.vue');
    const EquationMultiplication = () => import('./equation-multiplication/PlayBox.vue');
    const EquationOnline = () => import('./equation-online/PlayBox.vue');
    const Anzan = () => import('./anzan/PlayBox.vue');
    const AnzanWithTime = () => import('./anzan-with-time/PlayBox.vue');
    const Puzzle = () => import('./puzzle/PlayBox.vue');
    const Chains = () => import('./chains/PlayBox.vue');
    const Couples = () => import('./couples/PlayBox.vue');
    const Schulte = () => import('./schulte/PlayBox.vue');
    const SequenceOfNumbers = () => import('./sequence-of-numbers/PlayBox.vue');
    const AssembleTheNumber = () => import('./assemble-the-number/PlayBox.vue');
    const Scanner = () => import('./scanner/PlayBox.vue');
    const WordsField = () => import('./words-field/PlayBox.vue');
    const WordsFieldOnline = () => import('./words-field/PlayBox.vue');
    const WordsFieldReadingEn = () => import('./words-field-reading-en/PlayBox.vue');
    const Shutter = () => import('./shutter/PlayBox.vue');
    const ShutterOnline = () => import('./shutter/PlayBox.vue');
    const ShutterReadingEn = () => import('./shutter-reading-en/PlayBox.vue');
    const ShutterRussianLanguage = () => import('./shutter-russian-language/PlayBox.vue');
    const Anagrams = () => import('./anagrams/PlayBox.vue');
    const AnagramsOnline = () => import('./anagrams/PlayBox.vue');
    const Texts = () => import('./texts/PlayBox.vue');
    const LettersField = () => import('./letters-field/PlayBox.vue');
    const LettersFieldOnline = () => import('./letters-field/PlayBox.vue');
    const CatchWord = () => import('./catch-word/PlayBox.vue');
    const CatchWordReadingEn = () => import('./catch-word-reading-en/PlayBox.vue');
    const CatchWordOnline = () => import('./catch-word/PlayBox.vue');
    const VisionField = () => import('./vision-field/PlayBox.vue');
    const VisionFieldReadingEn = () => import('./vision-field-reading-en/PlayBox.vue');
    const Matrix = () => import('./matrix/PlayBox.vue');
    const Memorize = () => import('./memorize/PlayBox.vue');
    const KeenEye = () => import('./keen-eye/PlayBox.vue');
    const KeenEyeBonusGames = () => import('./keen-eye/PlayBox.vue');
    const NumericalNetwork = () => import('./numerical-network/PlayBox.vue');
    const Navigator = () => import('./navigator/PlayBox.vue');
    const NavigatorBonusGames = () => import('./navigator/PlayBox.vue');
    const Synonyms = () => import('./synonyms/PlayBox.vue');
    const SynonymsOnline = () => import('./synonyms/PlayBox.vue');
    const SynonymsReadingEn = () => import('./synonyms-reading-en/PlayBox.vue');
    const SynonymsRussianLanguage = () => import('./synonyms-russian-language/PlayBox.vue');
    const AntonymsRussianLanguage = () => import('./antonyms-russian-language/PlayBox.vue');
    const Presentations = () => import('./presentations/PlayBox.vue');
    const TextsOnline = () => import('./texts/PlayBox.vue');
    const Antonyms = () => import('./antonyms/PlayBox.vue');
    const Search = () => import('./search/PlayBox.vue');
    const Classifications = () => import('./classifications/PlayBox.vue');
    const SoundCouples = () => import('./sound-couples/PlayBox.vue');
    const DigitCouples = () => import('./digit-couples/PlayBox.vue');
    const FourthOne = () => import('./fourth-one/PlayBox.vue');
    const Memory = () => import('./memory/PlayBox.vue');
    const Abacus = () => import('./addition-and-subtraction/tools/Abacus.vue');
    const RootsAndDegrees = () => import('./roots-and-degrees/PlayBox.vue');
    const Expressions = () => import('./expressions/PlayBox.vue');
    const DayOfWeek = () => import('./day-of-week/PlayBox.vue');
    const RootsAndSquares = () => import('./roots-and-squares/PlayBox.vue');
    const Loner = () => import('./Loner/PlayBox.vue');
    const LonerBonusGames = () => import('../../games/bonus-games/loner/PlayBox.vue');
    const CouplesMemoryDevelopment = () => import('./couples/PlayBox.vue');
    const Memo = () => import('./memo/PlayBox.vue');
    const MemoBonusGames = () => import('./memo/PlayBox.vue');
    const MemoryX = () => import('./memory-x/PlayBox.vue');
    const RevivedNumbers = () => import('./revived-numbers/PlayBox.vue');
    const Associations = () => import('./associations/PlayBox.vue');
    const WhoIsWho = () => import('./who-is-who/PlayBox.vue');
    const CosmoTour = () => import('./cosmotour/PlayBox.vue');
    const SecretsOfCicero = () => import('./secrets-of-cicero/PlayBox.vue');
    const VocabularyWords = () => import('./vocabulary-words/PlayBox.vue');
    const VocabularyMemo = () => import('./vocabulary-memo/PlayBox.vue');
    const Alpari = () => import('./alpari/PlayBox.vue');
    const MentalDrawing = () => import('../../games/bonus-games/mental-drawing/PlayBox.vue');
    const HandsUp = () => import('../../games/bonus-games/hands-up/PlayBox.vue');
    const Chameleon = () => import('../../games/bonus-games/chameleon/PlayBox.vue');
    const RepeatAfterMe = () => import('../../games/bonus-games/repeat-after-me/PlayBox.vue');
    const CorrectiveSamples = () => import('../../games/bonus-games/corrective-samples/PlayBox.vue');
    const Scout = () => import('../../games/bonus-games/scout/PlayBox.vue');
    const ChainsBonusGame = () => import('../../games/bonus-games/chains-bonus-game/PlayBox.vue');
    const Diada = () => import('./diada/PlayBox.vue');
    const ChainsReadingEn = () => import('./chains-reading-en/PlayBox.vue');
    const CouplesReadingEn = () => import('./couples-reading-en/PlayBox.vue');
    const DivideItRight = () => import('./divide-it-right/PlayBox.vue');
    const ChooseItRight = () => import('./choose-it-right/PlayBox.vue');
    const DivideUnstressedVowels = () => import('./divide-unstressed-vowels/PlayBox.vue');
    const WordStructure = () => import('./word-structure/PlayBox.vue');
    const SentenceParsing = () => import('./sentence-parsing/PlayBox.vue');
    const VocabularyWordsRus = () => import('./vocabulary-words-rus/PlayBox.vue');
    const WordDrum = () => import('./word-drum/PlayBox.vue');
    const LettersSoundsSyllables = () => import('./letters-sounds-syllables/PlayBox.vue');
    const TransferSyllables = () => import('./transfer-syllables/PlayBox.vue');
    const TransferSyllablesSchoolPreparing = () => import('./transfer-syllables-school-preparing/PlayBox.vue');
    const HardSoftConsonants = () => import('./hard-soft-consonants/PlayBox.vue');
    const HardSoftConsonantsSchoolPreparing = () => import('./hard-soft-consonants-school-preparing/PlayBox.vue');
    const VocabularyMemoRus = () => import('./vocabulary-memo-rus/PlayBox.vue');
    const Orthograph = () => import('./orthograph/PlayBox.vue');
    const TranscriptionInWord = () => import('./transcription-in-word/PlayBox.vue');
    const VoicedAndDeafConsonants = () => import('./voiced-and-deaf-consonants/PlayBox.vue');
    const PartsOfSpeech = () => import('./parts-of-speech/PlayBox.vue');
    const ParsingSentencePartsSpeech = () => import('./parsing-sentence-parts-speech/PlayBox.vue');
    const GenericWords = () => import('./generic-words/PlayBox.vue');
    const VerbTenses = () => import('./verb-tenses/PlayBox.vue');
    const SortOneMany = () => import('./sort-one-many/PlayBox.vue');
    const PunctuationMarks = () => import('./punctuation-marks/PlayBox.vue');
    const Alphabet = () => import('./alphabet/PlayBox.vue');
    const ConnectThePuzzles = () => import('./connect-the-puzzles/PlayBox.vue');

    import { mapState } from 'vuex';
    import getTipsForCurrentParameters from '../../../utils/trainer/getTipsForCurrentParameters';

    const GAMES_ROUTES_FOR_ABACUS = [
        'addition-and-subtraction-with-time:start',
        'addition-and-subtraction:start',
        'equation:start',
        'factorization:start',
        'interactive-flash-cards:start',
        'multiplication-and-division:start',
        'anzan:start',
        'anzan-with-time:start',
        'roots-and-degrees:start',
    ];

    const GAMES_ROUTES_FOR_TIPS = [
        'addition-and-subtraction-math-magic:start',
        'multiplication-and-division-math-magic:start',
        'tabular-multiplication:start',
        'roots-and-squares:start',
    ];

    import { cloneDeep, isEqual } from 'lodash';
    import apiInstance from '@/api';
    import TariffsApi from '@/api/tariffs/tariffs';
    import {
        PLAYTIME_SYNC_INTERVAL_MS,
        PLAYTIME_TARIFF_RESTRICTION_APPLIES_TO_USER_TYPES,
        TARIFF_IDS_WITH_PLAY_TIME_LIMIT,
    } from '@/config/tariff_constants';

    export default {
        props: {
            type: String,
            exerciseId: [Number, String],
            homeworkId: [Number, String],
            playersNumber: Number,
            showTurtle: {
                type: Boolean,
                default: true,
            },
            gameType: {
                type: String,
                default: 'homework',
            },
            answerDuration: Number,
            checkMultiplayerAnswers: Boolean,
            playAgain: Boolean,
            commonParametersChangerCounter: {
                type: Number,
                default: 0,
            },
            multiplayerGamesState: {
                type: String,
                default: 'paused',
            },
            refreshGameEvent: {
                type: Boolean,
                default: false,
            },
            continueGameEvent: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                playTimerId: null,
                playtimeSyncTimerId: null,
                playtimeSeconds: 0,
                playtimeSyncInFlight: false,
                treatAsClick: false,
                colorsForStudents: [
                    '#6BBD6C',
                    '#2187FF',
                    '#FF7245',
                    '#FFAD1F',
                    '#FF5DAB',
                    '#42D29E',
                    '#BB6BD9',
                    '#56CCF2',
                ],
                multiplayerBorderColor: 'hsl(240, 55%, 94%)',
                showDraggableContent: false,
                currentAbacusNumber: 0,
                tipsForCurrentParameters: null,
                difficulties: [
                    {
                        name: 'Однозначные цифры',
                        values: [1, 'digits'],
                    },
                    {
                        name: 'Двузначные цифры',
                        values: [2, 'teens', 'tens', 'twoDigit'],
                    },
                    {
                        name: 'Трехзначные цифры',
                        values: [3, 'hundreds', 'threeDigit'],
                    },
                    {
                        name: 'Четырехзначные цифры',
                        values: [4, 'thousands', 'fourDigit'],
                    },
                    {
                        name: 'Пятизначные цифры',
                        values: [5, 'fiveDigit'],
                    },
                    {
                        name: 'Шестизначные цифры',
                        values: [6, 'sixDigit'],
                    },
                    {
                        name: 'Семизначные цифры',
                        values: [7, 'sevenDigit'],
                    },
                    {
                        name: 'Восьмизначные цифры',
                        values: [8, 'eightDigit'],
                    },
                ],
                isGameWithoutBackground: false,
            };
        },
        created() {
            this.startPlayTimer();
            this.startPlaytimeSync();
        },
        mounted() {
            if (this.soloGame && !this.isGameWithoutBackground) {
                this.$refs.gameWrapper.style['background-color'] = this.backgroundColor;
            }
            if (this.isActiveAbacusButton) {
                let html = document.querySelector('html');
                if (html) {
                    html.style.cssText.replace('overflow: auto', '');
                }
            }
            window.addEventListener('pagehide', this.handlePageHide);
        },
        watch: {
            paramsArray: {
                handler() {
                    this.tipsForCurrentParameters = getTipsForCurrentParameters(_.cloneDeep(this.paramsArray?.[0]));
                },
                deep: true,
                immediate: true,
            },
            tipsForCurrentParameters(val, oldVal) {
                if (!isEqual(val, oldVal)) {
                    this.showDraggableContent = false;
                }
            },
        },
        computed: {
            isWhiteBackgroundNeeded() {
                return ['MentalDrawing', 'HandsUp', 'Chameleon', 'RepeatAfterMe','ConnectThePuzzles'].includes(this.type);
            },
            ...mapState({
                paramsArray: state => state.trainer.paramsArray,
                user: state => state.auth.user,
                olympiad: state => state.olympiad.olympiad,
                homework: state => state.trainer.homework,
            }),
            soloGame() {
                return !this.playersNumber || this.playersNumber === 1;
            },
            backgroundColor() {
                return this.$route.name.includes('catch-word') ? 'hsl(0, 0%, 100%)' : '#f7f7ff';
            },
            isHomework() {
                return this.gameType === 'homework' && this.exerciseId;
            },
            multiplayerClasses() {
                return {
                    'multiplayer--multiline':
                        (this.playersNumber > 4 || (this.playersNumber === 4 && window.innerWidth <= 1920)) &&
                        !this.isColumnsGame,
                };
            },
            isColumnsGame() {
                return ['AdditionAndSubtraction', 'Anzan'].includes(this.type);
            },
            isActiveAbacusButton() {
                return GAMES_ROUTES_FOR_ABACUS.includes(this.$route.name);
            },
            isActiveTipsButton() {
                return GAMES_ROUTES_FOR_TIPS.includes(this.$route.name) && this.tipsForCurrentParameters.length;
            },
            gameDifficult() {
                if (this.paramsArray) {
                    let lastHigherElObj = cloneDeep(this.paramsArray[0]);
                    let diffObj = {
                        'Однозначные цифры': 1,
                        'Двузначные цифры': 2,
                        'Трехзначные цифры': 3,
                        'Четырехзначные цифры': 4,
                        'Пятизначные цифры': 5,
                        'Шестизначные цифры': 6,
                        'Семизначные цифры': 7,
                        'Восьмизначные цифры': 8,
                    };
                    let arrayOfDifficulties = [];
                    let maxDigitCapacity = 0;
                    this.paramsArray.forEach(param => {
                        if (this.type === 'InteractiveFlashCards') {
                            for (const key of Object.keys(param.difficult)) {
                                if (param.difficult[key] && !arrayOfDifficulties.includes(key)) {
                                    arrayOfDifficulties.push(key);
                                }
                            }
                            return;
                        }
                        if (
                            this.type === 'MultiplicationAndDivision' ||
                            this.type === 'MultiplicationAndDivisionMathMagic' ||
                            this.type === 'TabularMultiplication'
                        ) {
                            if (param.digitCapacity.left.length || param.digitCapacity.right.length) {
                                let max = Math.max(...param.digitCapacity.left, ...param.digitCapacity.right);
                                if (maxDigitCapacity < max) {
                                    maxDigitCapacity = max;
                                }
                                return;
                            }
                        }
                        if (this.type === 'RootsAndDegrees') {
                            let max = param.digitCapacity;
                            if (maxDigitCapacity < max) {
                                maxDigitCapacity = max;
                            }
                            return;
                        }
                        if (diffObj[param.difficult] > diffObj[lastHigherElObj.difficult] || !lastHigherElObj) {
                            lastHigherElObj = param;
                        }
                    });
                    if (this.type === 'InteractiveFlashCards') {
                        let maxDifficult =
                            diffObj[
                                this.difficulties.find(difficult => difficult.values.includes(arrayOfDifficulties[0]))
                                    .name
                            ];
                        arrayOfDifficulties.forEach(difficult => {
                            let comparingValue =
                                diffObj[
                                    this.difficulties.find(localDifficult => localDifficult.values.includes(difficult))
                                        .name
                                ];
                            if (maxDifficult < comparingValue) {
                                maxDifficult = comparingValue;
                            }
                        });
                        return maxDifficult;
                    }
                    if (
                        this.type === 'MultiplicationAndDivision' ||
                        this.type === 'MultiplicationAndDivisionMathMagic' ||
                        this.type === 'TabularMultiplication'
                    ) {
                        if (maxDigitCapacity !== 0) {
                            return diffObj[
                                this.difficulties.find(difficult => difficult.values.includes(maxDigitCapacity)).name
                            ];
                        }
                        return diffObj['Двузначные цифры'];
                    }
                    if (this.type === 'RootsAndDegrees') {
                        if (maxDigitCapacity !== 0) {
                            return diffObj[
                                this.difficulties.find(difficult => difficult.values.includes(maxDigitCapacity)).name
                            ];
                        }
                    }
                    return lastHigherElObj.difficult;
                }
                return '';
            },
        },
        beforeDestroy() {
            window.removeEventListener('pagehide', this.handlePageHide);
            this.stopPlaytimeSync();
            this.stopPlayTimer();
            if (!this.olympiad && !this.homework) {
                this.sendPlayTimeOnUnload();
            }
            this.$root.playAgainReadyScreens = 0;
            this.$root.checkAnswersReadyScreens = 0;
        },
        methods: {
            startPlayTimer() {
                if (this.playTimerId) return;
                this.playTimerId = setInterval(() => {
                    this.playtimeSeconds += 1;
                }, 1000);
            },

            stopPlayTimer() {
                if (this.playTimerId) {
                    clearInterval(this.playTimerId);
                    this.playTimerId = null;
                }
            },
            startPlaytimeSync() {
                if (this.playtimeSyncTimerId || this.olympiad || this.homework || !this.shouldSendPlayTime()) {
                    return;
                }
                this.playtimeSyncTimerId = setInterval(() => {
                    this.sendPlayTime();
                }, PLAYTIME_SYNC_INTERVAL_MS);
            },
            stopPlaytimeSync() {
                if (this.playtimeSyncTimerId) {
                    clearInterval(this.playtimeSyncTimerId);
                    this.playtimeSyncTimerId = null;
                }
            },
            shouldSendPlayTime() {
                const school = this.user && this.user.schools && this.user.schools[0];
                if (!school) return false;
                const schoolTariffId = Number(school.tariffId);
                const userType = this.user.type;
                return (
                    TARIFF_IDS_WITH_PLAY_TIME_LIMIT.includes(schoolTariffId) &&
                    PLAYTIME_TARIFF_RESTRICTION_APPLIES_TO_USER_TYPES.includes(userType)
                );
            },
            takePlaytimeDelta() {
                const delta = Math.min(10000, Math.max(0, Math.floor(this.playtimeSeconds)));
                if (!delta) return 0;
                this.playtimeSeconds -= delta;
                return delta;
            },
            applyPlaytimeRemaining(remaining) {
                if (remaining == null || !this.user) return;
                const tariffSnapshot = Object.assign({}, this.user.tariffSnapshot || {}, {
                    playtime: Number(remaining),
                });
                this.$store.commit('setUser', Object.assign({}, this.user, { tariffSnapshot }));
            },
            handlePlaytimeExhausted() {
                this.stopPlayTimer();
                this.stopPlaytimeSync();
                if (this.$route.name !== 'user:playtime-ended') {
                    this.$router.replace({ name: 'user:playtime-ended' });
                }
            },
            async sendPlayTime() {
                if (this.olympiad || this.homework || !this.shouldSendPlayTime()) return;
                if (this.playtimeSyncInFlight) return;

                const schoolId = this.user.schools[0].id;
                const playtime = this.takePlaytimeDelta();
                if (!playtime || !schoolId) return;

                this.playtimeSyncInFlight = true;
                try {
                    const response = await TariffsApi.updatePlayTime({
                        playtime,
                        schoolId,
                    });
                    const remaining =
                        response.playtime != null
                            ? response.playtime
                            : response.userTariffRestrictionSnapshotData &&
                              response.userTariffRestrictionSnapshotData.playtime;
                    this.applyPlaytimeRemaining(remaining);
                    if (remaining != null && Number(remaining) <= 0) {
                        this.handlePlaytimeExhausted();
                    }
                } catch (error) {
                    this.playtimeSeconds += playtime;
                    console.error('Failed to update playtime:', error);
                } finally {
                    this.playtimeSyncInFlight = false;
                }
            },
            sendPlayTimeOnUnload() {
                if (this.olympiad || this.homework || !this.shouldSendPlayTime()) return;

                const playtime = this.takePlaytimeDelta();
                const schoolId = this.user.schools[0] && this.user.schools[0].id;

                if (!playtime || !schoolId) return;

                try {
                    const url = `${api.env.API_ENDPOINT}/tariffs/update-play-time`;
                    fetch(url, {
                        method: 'PUT',
                        keepalive: true,
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: `application/cs-api.v${env.apiVersion}+json, text/plain, */*`,
                            Authorization: apiInstance.axios.defaults.headers.common['Authorization'],
                        },
                        body: JSON.stringify({
                            playtime,
                            school_id: schoolId,
                        }),
                    });
                } catch (error) {
                    this.playtimeSeconds += playtime;
                    console.error('Failed to update playtime on unload:', error);
                }
            },
            handlePageHide() {
                this.stopPlaytimeSync();
                this.stopPlayTimer();
                this.sendPlayTimeOnUnload();
            },
            handleMousedown() {
                this.treatAsClick = true;
                setTimeout(() => {
                    this.treatAsClick = false;
                }, 150);
            },
            afterEnter(el) {
                let coords = this.getCoords(el);

                if (coords.top < 10) {
                    el.parentElement.style.right = 'auto';
                    el.parentElement.style.bottom = 'auto';
                    el.parentElement.style.top = 10 + 'px';
                }

                if (coords.left < 10) {
                    el.parentElement.style.right = 'auto';
                    el.parentElement.style.bottom = 'auto';
                    el.parentElement.style.left = 10 + 'px';
                }
            },
            leaveElementAnimation(el, done) {
                done();
            },
            handleIsCountdownActive(isActive) {
                this.$emit('isCountdownActive', isActive);
            },
            changeAbacusNumber(val) {
                this.currentAbacusNumber = val;
            },
            resetAbacusNumber() {
                this.currentAbacusNumber = 0;
            },
            changeShowDraggableContentVariable() {
                if (this.treatAsClick) {
                    this.showDraggableContent = !this.showDraggableContent;
                }
            },
            getCoords(elem) {
                let box = elem.getBoundingClientRect();
                return {
                    top: box.top + pageYOffset,
                    left: box.left + pageXOffset,
                    width: box.width,
                    height: box.height,
                };
            },
            classForCurrentAmountOfPlayers(index) {
                return Object.assign(
                    {
                        'multiplayer__item--is-12': this.playersNumber === 1 && !this.isColumnsGame,
                        'multiplayer__item--is-6':
                            (this.playersNumber === 2 ||
                                this.playersNumber === 4 ||
                                (this.playersNumber === 5 && index > 3)) &&
                            !this.isColumnsGame,
                        'multiplayer__item--is-4':
                            (this.playersNumber === 3 ||
                                (this.playersNumber === 5 && index <= 3) ||
                                this.playersNumber === 6 ||
                                (this.playersNumber === 7 && index > 4)) &&
                            !this.isColumnsGame,
                        'multiplayer__item--is-3':
                            ((this.playersNumber === 4 && window.innerWidth >= 1920) ||
                                (this.playersNumber === 7 && index <= 4) ||
                                this.playersNumber === 8) &&
                            !this.isColumnsGame,
                    },
                    { [`multiplayer__item_${this.playersNumber}-players`]: true }
                );
            },
        },
        components: {
            NextExercise,
            Tips,
            TipButton,
            AdditionAndSubtraction,
            EquationMultiplication,
            AdditionAndSubtractionWithTime,
            AdditionAndSubtractionMathMagic,
            FlashCards,
            InteractiveFlashCards,
            MultiplicationAndDivision,
            MultiplicationAndDivisionMathMagic,
            TabularMultiplication,
            Equation,
            Factorization,
            EquationOnline,
            Anzan,
            AnzanWithTime,
            Puzzle,
            Chains,
            Couples,
            Schulte,
            SequenceOfNumbers,
            AssembleTheNumber,
            Scanner,
            WordsField,
            WordsFieldOnline,
            WordsFieldReadingEn,
            Shutter,
            ShutterOnline,
            ShutterReadingEn,
            ShutterRussianLanguage,
            Anagrams,
            AnagramsOnline,
            Texts,
            Expressions,
            LettersField,
            LettersFieldOnline,
            CatchWord,
            CatchWordOnline,
            CatchWordReadingEn,
            VisionField,
            VisionFieldReadingEn,
            Matrix,
            Memorize,
            KeenEye,
            KeenEyeBonusGames,
            NumericalNetwork,
            Navigator,
            NavigatorBonusGames,
            Synonyms,
            Presentations,
            SynonymsReadingEn,
            SynonymsOnline,
            SynonymsRussianLanguage,
            AntonymsRussianLanguage,
            TextsOnline,
            Antonyms,
            Search,
            Classifications,
            SoundCouples,
            DigitCouples,
            FourthOne,
            Abacus,
            Memory,
            RootsAndDegrees,
            DayOfWeek,
            RootsAndSquares,
            Loner,
            LonerBonusGames,
            CouplesMemoryDevelopment,
            Memo,
            MemoBonusGames,
            MemoryX,
            RevivedNumbers,
            Associations,
            WhoIsWho,
            SecretsOfCicero,
            VocabularyWords,
            VocabularyMemo,
            CosmoTour,
            MentalDrawing,
            HandsUp,
            Chameleon,
            RepeatAfterMe,
            CorrectiveSamples,
            Scout,
            Diada,
            Alpari,
            ChainsBonusGame,
            ChainsReadingEn,
            CouplesReadingEn,
            TransferSyllables,
            TransferSyllablesSchoolPreparing,
            HardSoftConsonants,
            HardSoftConsonantsSchoolPreparing,
            VocabularyMemoRus,
            Orthograph,
            TranscriptionInWord,
            Alphabet,
            ConnectThePuzzles,
            DivideItRight,
            ChooseItRight,
            DivideUnstressedVowels,
            WordStructure,
            SentenceParsing,
            VocabularyWordsRus,
            WordDrum,
            LettersSoundsSyllables,
            VoicedAndDeafConsonants,
            PartsOfSpeech,
            ParsingSentencePartsSpeech,
            GenericWords,
            VerbTenses,
            SortOneMany,
            PunctuationMarks,
        },
    };
</script>

<style lang="scss" scoped>
    .games-wrapper {
        margin: 0;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        flex-direction: column;
        height: 100%;
        min-height: calc(100vh - 179px);
    }

    .games-wrapper--solo {
        background-color: $hsl-color-gray-98;
        border-radius: $border-radius-20;
        padding: 0;
        @include media($sm) {
            padding: 15px 20px;
        }
        @include media($md) {
            padding: 30px;
        }
    }

    .games-wrapper--without-background {
        background: transparent;
    }

    .games-wrapper--white-background {
        background: white !important;
    }

    .games-wrapper--multiplayer {
        margin: 0 -10px;
    }

    .multiplayer {
        display: flex;
        box-sizing: border-box;
        flex: 1 1 auto;
        width: 100%;
        flex-direction: row;
        transition: all 0.3s linear;
    }

    .multiplayer--multiline {
        flex-wrap: wrap;
    }

    .multiplayer__item {
        display: flex;
        padding: 10px;
        box-sizing: border-box;
        flex-grow: 1;
    }

    $multiplayer-sizes: (
        '12': (
            100%,
        ),
        '6': (
            50%,
        ),
        '4': (
            33.3%,
        ),
        '3': (
            25%,
        ),
    );

    @each $col-size, $size in $multiplayer-sizes {
        @if $col-size = 3 {
            .multiplayer__item {
                @media screen and (max-width: 1439px) {
                    padding: 5px;
                }
            }
        }
        .multiplayer__item--is-#{$col-size} {
            flex-basis: $size;
            max-width: $size;
        }
    }

    .games-wrapper__content {
        display: flex;
        height: 100%;
        width: 100%;
        justify-content: center;
        background-color: $hsl-color-gray-98_4;
        border: 2px solid $hsl-color-gray-98_4;
        border-radius: $border-radius-40;
        transition: all 0.1s linear;
        padding: 20px;
        box-sizing: border-box;
    }

    .games-wrapper__content--small {
        padding: 10px;
        border-radius: $border-radius-20;
        @include media($xl) {
            padding: 20px 0 0 0;
        }
        @include media($xlg) {
            padding: 5px 0 0 0;
        }
    }

    .play-box__correct-answer {
        border: 2px solid $hsl-color-green-58;
    }

    .play-box__incorrect-answer {
        border: 2px solid $hsl-color-red-62;
    }

    .fade-enter {
        opacity: 0;
        transform: translate(200px);
    }

    .fade-enter-active {
        transition: all 0.5s;
    }

    .fade-leave-active {
        transition: all 0.3s;
        opacity: 0;
        transform: translateY(0);
        position: absolute;
    }
    .fade-leave-to {
        transform: translate(200px);
    }

    .fade-move {
        transition: all 0.5s cubic-bezier(0.77, 0, 0.175, 1);
    }

    .games-wrapper__abacus {
        padding: 30px 120px 10px;
        height: 350px;
        @include media($xl) {
            height: 430px;
        }
    }

    .games-wrapper__abacus-button {
        display: flex;
        align-items: center;
        padding: 20px 40px;
        transition: background-color 0.3s ease;
        cursor: pointer;
        border-radius: $border-radius-40;
        &:hover {
            background-color: darken($hsl-color-gray-100, 5%);
        }
        &--active {
            background-color: darken($hsl-color-gray-100, 5%);
        }
        &--active:hover {
            background-color: darken($hsl-color-gray-100, 10%);
        }
    }
    $tips-margin-bottom: -22px;
    .games-wrapper__tips {
        @include media($sm) {
            margin-bottom: $tips-margin-bottom;
        }
    }

    .games-wrapper__tip-button_active {
        margin-right: 15px;
        @include media($sm) {
            margin-right: 0;
        }
    }

    .abacus__arrow-icon {
        color: $hsl-color-blue-56_5;
        margin-right: 10px;
    }

    .abacus__icon {
        color: #71c372;
        margin-right: 10px;
    }

    .slide-fade-enter-to,
    .slide-fade-leave {
        opacity: 1;
        max-width: 1000px;
    }

    .slide-fade-enter-active {
        transition: height 0.2s linear 0.2s, opacity 0.2s linear 0.2s, padding-top 0.2s linear 0.2s,
            padding-bottom 0.2s linear 0.2s, max-width 0.2s linear;
        overflow: hidden;
    }

    .slide-fade-leave-active {
        transition: height 0.2s linear, opacity 0.2s linear, padding-top 0.2s linear, padding-bottom 0.2s linear,
            max-width 0.2s linear 0.2s;
        overflow: hidden;
    }

    .slide-fade-enter,
    .slide-fade-leave-to {
        opacity: 0;
        height: 0;
        padding-top: 0;
        padding-bottom: 0;
        max-width: 0;
        @include media($sm) {
            height: -$tips-margin-bottom;
        }
    }
</style>
